import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Package, ClipboardText } from "@phosphor-icons/react";
import { api, apiError, fmtMoney } from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const RESOURCE_TYPES = ["labor", "equipment", "material", "explosive", "service", "other"];
const PHASES = ["development", "drilling", "hauling", "ground_support", "services", "ventilation", "other"];
const EMPTY_RESOURCE = { name: "", type: "material", unit: "unit", unit_cost: 0, description: "" };
const EMPTY_TEMPLATE = { name: "", phase: "development", duration_days: 1, description: "", resources: [] };

export default function CatalogSettings() {
  const { t } = useLang();
  const [resources, setResources] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [resDialogOpen, setResDialogOpen] = useState(false);
  const [tplDialogOpen, setTplDialogOpen] = useState(false);
  const [resEditing, setResEditing] = useState(null);
  const [tplEditing, setTplEditing] = useState(null);
  const [resForm, setResForm] = useState(EMPTY_RESOURCE);
  const [tplForm, setTplForm] = useState(EMPTY_TEMPLATE);

  const load = () => {
    api.get("/resources").then((r) => setResources(r.data)).catch(() => {});
    api.get("/task-templates").then((r) => setTemplates(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const saveResource = async () => {
    try {
      const payload = { ...resForm, unit_cost: Number(resForm.unit_cost) };
      if (resEditing) await api.put(`/resources/${resEditing}`, payload);
      else await api.post("/resources", payload);
      toast.success("✓");
      setResDialogOpen(false); setResEditing(null); setResForm(EMPTY_RESOURCE);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const removeResource = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/resources/${id}`); load();
  };

  const saveTemplate = async () => {
    try {
      const payload = { ...tplForm, duration_days: Number(tplForm.duration_days),
        resources: tplForm.resources.map((r) => ({ ...r, quantity: Number(r.quantity) })) };
      if (tplEditing) await api.put(`/task-templates/${tplEditing}`, payload);
      else await api.post("/task-templates", payload);
      toast.success("✓");
      setTplDialogOpen(false); setTplEditing(null); setTplForm(EMPTY_TEMPLATE);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const removeTemplate = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/task-templates/${id}`); load();
  };
  const addResourceToTemplate = () => {
    setTplForm((s) => ({ ...s, resources: [...s.resources, { name: "", quantity: 1, unit: "unit", note: "" }] }));
  };
  const removeResourceFromTemplate = (idx) => {
    setTplForm((s) => ({ ...s, resources: s.resources.filter((_, i) => i !== idx) }));
  };
  const updateTemplateResource = (idx, patch) => {
    setTplForm((s) => ({ ...s, resources: s.resources.map((r, i) => (i === idx ? { ...r, ...patch } : r)) }));
  };

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-4xl" data-testid="resources-section">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Package size={20} weight="duotone" className="text-blue-900" />
            <div>
              <Label className="overline-label">{t("resourcesCatalog")}</Label>
              <p className="text-sm text-slate-500">{t("resourcesHint")}</p>
            </div>
          </div>
          <Button data-testid="new-resource-btn" onClick={() => { setResEditing(null); setResForm(EMPTY_RESOURCE); setResDialogOpen(true); }} className="bg-blue-900 hover:bg-blue-800 gap-1.5">
            <Plus size={14} weight="bold" /> {t("newResource")}
          </Button>
        </div>
        {resources.length ? (
          <table className="w-full data-table">
            <thead><tr><th>{t("name")}</th><th>{t("type")}</th><th>{t("unit")}</th><th>{t("unitCost")}</th><th></th></tr></thead>
            <tbody>
              {resources.map((r) => (
                <tr key={r.id} data-testid={`resource-row-${r.id}`}>
                  <td className="font-medium">{r.name}<div className="text-xs text-slate-400">{r.description}</div></td>
                  <td className="text-slate-500 text-xs uppercase tracking-wider">{t(`res_${r.type}`) || r.type}</td>
                  <td className="text-xs font-mono">{r.unit}</td>
                  <td className="text-xs font-mono">{fmtMoney(r.unit_cost)}</td>
                  <td className="whitespace-nowrap">
                    <button data-testid={`resource-edit-${r.id}`} className="text-slate-400 hover:text-blue-900 mr-2"
                      onClick={() => { setResEditing(r.id); setResForm({ name: r.name, type: r.type, unit: r.unit, unit_cost: r.unit_cost, description: r.description || "" }); setResDialogOpen(true); }}>
                      <PencilSimple size={14} />
                    </button>
                    <button data-testid={`resource-delete-${r.id}`} className="text-slate-400 hover:text-red-500" onClick={() => removeResource(r.id)}><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="text-sm text-slate-400 py-6 text-center" data-testid="resources-empty">{t("noResources")}</p>}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-4xl" data-testid="templates-section">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <ClipboardText size={20} weight="duotone" className="text-blue-900" />
            <div>
              <Label className="overline-label">{t("taskTemplates")}</Label>
              <p className="text-sm text-slate-500">{t("templatesHint")}</p>
            </div>
          </div>
          <Button data-testid="new-template-btn" onClick={() => { setTplEditing(null); setTplForm(EMPTY_TEMPLATE); setTplDialogOpen(true); }} className="bg-blue-900 hover:bg-blue-800 gap-1.5">
            <Plus size={14} weight="bold" /> {t("newTemplate")}
          </Button>
        </div>
        {templates.length ? (
          <div className="grid md:grid-cols-2 gap-3">
            {templates.map((tpl) => (
              <div key={tpl.id} className="border border-slate-200 rounded-md p-3" data-testid={`template-card-${tpl.id}`}>
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm text-slate-900 truncate">{tpl.name}</p>
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">{t(tpl.phase) || tpl.phase} · {tpl.duration_days}d</p>
                    <p className="text-xs text-slate-500 mt-1">{tpl.description}</p>
                    {tpl.resources?.length > 0 && (
                      <ul className="text-[11px] text-slate-600 mt-2 space-y-0.5">
                        {tpl.resources.slice(0, 5).map((r, i) => (
                          <li key={i}>• {r.quantity} {r.unit} — {r.name}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button data-testid={`template-edit-${tpl.id}`} className="text-slate-400 hover:text-blue-900" onClick={() => { setTplEditing(tpl.id); setTplForm({ name: tpl.name, phase: tpl.phase, duration_days: tpl.duration_days, description: tpl.description || "", resources: tpl.resources || [] }); setTplDialogOpen(true); }}>
                      <PencilSimple size={14} />
                    </button>
                    <button data-testid={`template-delete-${tpl.id}`} className="text-slate-400 hover:text-red-500" onClick={() => removeTemplate(tpl.id)}><Trash size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-slate-400 py-6 text-center" data-testid="templates-empty">{t("noTemplates")}</p>}
      </div>

      {/* Resource dialog */}
      <Dialog open={resDialogOpen} onOpenChange={setResDialogOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="resource-dialog">
          <DialogHeader><DialogTitle>{resEditing ? t("editResource") : t("newResource")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5"><Label className="overline-label">{t("name")}</Label>
              <Input data-testid="resource-name-input" value={resForm.name} onChange={(e) => setResForm({ ...resForm, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="overline-label">{t("type")}</Label>
              <Select value={resForm.type} onValueChange={(v) => setResForm({ ...resForm, type: v })}>
                <SelectTrigger data-testid="resource-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{RESOURCE_TYPES.map((v) => <SelectItem key={v} value={v}>{t(`res_${v}`) || v}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="overline-label">{t("unit")}</Label>
              <Input data-testid="resource-unit-input" value={resForm.unit} onChange={(e) => setResForm({ ...resForm, unit: e.target.value })} placeholder="kg, h, m³, unit" /></div>
            <div className="col-span-2 space-y-1.5"><Label className="overline-label">{t("unitCost")}</Label>
              <Input type="number" step="0.01" data-testid="resource-unitcost-input" value={resForm.unit_cost} onChange={(e) => setResForm({ ...resForm, unit_cost: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label className="overline-label">{t("description")}</Label>
              <Input data-testid="resource-description-input" value={resForm.description} onChange={(e) => setResForm({ ...resForm, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResDialogOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="save-resource-btn" onClick={saveResource} className="bg-blue-900 hover:bg-blue-800">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template dialog */}
      <Dialog open={tplDialogOpen} onOpenChange={setTplDialogOpen}>
        <DialogContent className="bg-white max-w-2xl" data-testid="template-dialog">
          <DialogHeader><DialogTitle>{tplEditing ? t("editTemplate") : t("newTemplate")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5"><Label className="overline-label">{t("name")}</Label>
              <Input data-testid="template-name-input" value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="overline-label">{t("phase")}</Label>
              <Select value={tplForm.phase} onValueChange={(v) => setTplForm({ ...tplForm, phase: v })}>
                <SelectTrigger data-testid="template-phase-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{PHASES.map((p) => <SelectItem key={p} value={p}>{t(p)}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="overline-label">{t("durationDays")}</Label>
              <Input type="number" min={1} data-testid="template-duration-input" value={tplForm.duration_days} onChange={(e) => setTplForm({ ...tplForm, duration_days: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label className="overline-label">{t("description")}</Label>
              <Input data-testid="template-description-input" value={tplForm.description} onChange={(e) => setTplForm({ ...tplForm, description: e.target.value })} /></div>
            <div className="col-span-2 space-y-2 border-t border-slate-100 pt-3">
              <div className="flex items-center justify-between">
                <Label className="overline-label">{t("resourcesNeeded")}</Label>
                <Button size="sm" variant="outline" data-testid="add-template-resource-btn" onClick={addResourceToTemplate} className="gap-1 h-7">
                  <Plus size={12} /> {t("addResource")}
                </Button>
              </div>
              {tplForm.resources.length === 0 && <p className="text-xs text-slate-400" data-testid="template-resources-empty">{t("noResourcesInTemplate")}</p>}
              {tplForm.resources.map((r, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`template-resource-row-${idx}`}>
                  <Input className="col-span-5" data-testid={`tpl-res-name-${idx}`} placeholder={t("name")} value={r.name} onChange={(e) => updateTemplateResource(idx, { name: e.target.value })}
                    list="resource-catalog"
                  />
                  <Input className="col-span-2" data-testid={`tpl-res-qty-${idx}`} type="number" step="0.1" placeholder={t("quantity")} value={r.quantity} onChange={(e) => updateTemplateResource(idx, { quantity: e.target.value })} />
                  <Input className="col-span-2" data-testid={`tpl-res-unit-${idx}`} placeholder={t("unit")} value={r.unit} onChange={(e) => updateTemplateResource(idx, { unit: e.target.value })} />
                  <Input className="col-span-2" data-testid={`tpl-res-note-${idx}`} placeholder={t("note")} value={r.note || ""} onChange={(e) => updateTemplateResource(idx, { note: e.target.value })} />
                  <button className="col-span-1 text-slate-400 hover:text-red-500 justify-self-center" data-testid={`tpl-res-remove-${idx}`} onClick={() => removeResourceFromTemplate(idx)}><Trash size={14} /></button>
                </div>
              ))}
              <datalist id="resource-catalog">
                {resources.map((r) => <option key={r.id} value={r.name} />)}
              </datalist>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTplDialogOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="save-template-btn" onClick={saveTemplate} className="bg-blue-900 hover:bg-blue-800">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
